import { Children, isValidElement } from 'react';
import { Box, Stack } from '@mui/material';
import { colorTokens, radiusTokens } from '../app/designTokens';
import { Select, SelectOption } from './ui/Select';

const { semantic } = colorTokens;

/**
 * Shared App / FilterBar composite (Simple).
 * Pass search + filter controls as children; keep page-specific filter logic outside.
 */
export function FilterBar({ children, type = 'Simple', sx }) {
  return (
    <Box
      data-filter-bar-type={type}
      sx={{
        mb: 3,
        p: 2.5,
        borderRadius: `${radiusTokens.card}px`,
        bgcolor: semantic.bg.surface,
        border: `1px solid ${semantic.border.subtle}`,
        ...sx,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        flexWrap="wrap"
        useFlexGap
        sx={{
          '& > .MuiTextField-root, & > .MuiFormControl-root': {
            minWidth: { xs: '100%', sm: 200 },
            flex: { sm: '1 1 200px' },
            maxWidth: { sm: 480 },
          },
        }}
      >
        {children}
      </Stack>
    </Box>
  );
}

function findOptionLabel(children, selected) {
  let matched;
  Children.forEach(children, (child) => {
    if (!isValidElement(child) || matched != null) {
      return;
    }
    if (String(child.props.value ?? '') !== String(selected ?? '')) {
      return;
    }
    const content = child.props.children;
    if (typeof content === 'string' || typeof content === 'number') {
      matched = String(content);
    }
  });
  return matched;
}

/**
 * Filter-oriented Select (Form / Select Compact) for FilterBar.
 * Prefer importing Select from ui/Select for non-filter contexts.
 *
 * Closed-value clarity:
 * - When `hideLabel` is set, closed display defaults to `${label}: ${optionLabel}`
 *   (e.g. "Stav: Všetko") unless a custom `renderValue` is provided.
 * - Optional `displayPrefix` overrides the prefix used in that closed display.
 * - API `value` is unchanged; this is display-only.
 */
export function FilterSelect({
  label,
  displayPrefix,
  value,
  onChange,
  children,
  minWidth = 160,
  hideLabel = false,
  renderValue,
  ...rest
}) {
  const prefix = displayPrefix ?? (hideLabel && label ? label : null);

  const contextualRenderValue = prefix
    ? (selected) => {
      const optionLabel = findOptionLabel(children, selected)
        ?? (selected == null || selected === '' ? '' : String(selected));
      return `${prefix}: ${optionLabel}`;
    }
    : undefined;

  return (
    <Select
      density="Compact"
      label={label}
      hideLabel={hideLabel}
      value={value}
      onChange={onChange}
      minWidth={minWidth}
      renderValue={renderValue ?? contextualRenderValue}
      {...rest}
    >
      {children}
    </Select>
  );
}

export { SelectOption as FilterSelectOption };
